import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, X, Send, Loader2, Sparkles, Lock, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { useTenantPlan } from '@/hooks/useTenantPlan';

// Routes where the floating AI button overlaps the page's own bottom-
// right control (chat composer, lead WhatsApp panel send button etc.).
// Hide on these pages so the composer stays clickable.
const HIDDEN_PATH_PREFIXES = [
  '/whatsapp',
  // Lead Detail has an inline WhatsApp chat panel anchored bottom-right —
  // the floating Ai button covers its Send control. Hide here too. Note:
  // the /leads list page (no id) is not affected because we only match
  // detail routes via the trailing slash.
  '/leads/',
];

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

const QUICK_ACTIONS = [
  'Show hot leads',
  'Inactive leads',
  'Conversion stats',
  'Draft follow-up email',
];

export function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Hide on routes whose own UI sits in the same bottom-right corner
  // (WhatsApp chat composer, etc.). Render nothing — saves the user
  // from chasing the floating button to reach the send control.
  if (HIDDEN_PATH_PREFIXES.some(p => location.pathname.startsWith(p))) {
    return null;
  }

  // The /ai/* endpoints are gated by Growth+. If the tenant doesn't have it,
  // render a compact upsell inside the widget body instead of letting the
  // user type a query that would 402 and pop the global upgrade modal on
  // top of an already-open chat panel.
  const { hasFeature, planName, loading: planLoading } = useTenantPlan();
  const hasAi = hasFeature('ai_assistant');

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendQuery = async (query: string) => {
    if (!query.trim() || !hasAi) return;
    const userMsg: Message = { role: 'user', text: query };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data } = await api.post('/ai/query', { query });
      setMessages(prev => [...prev, { role: 'assistant', text: data.text }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I could not process your request. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        // Stacked above the support-chat launcher (fixed bottom-5 right-5) so
        // the two floating buttons don't overlap in the bottom-right corner.
        className="fixed bottom-24 right-6 z-40 h-14 w-14 rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-110"
        title="AI Assistant"
      >
        <Sparkles className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] h-[550px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#6366F1] text-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          <div>
            <span className="text-sm font-semibold">AI Assistant</span>
            <span className="text-[10px] text-indigo-200 block">Powered by Claude</span>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-lg p-1">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Locked state — Growth+ only. Renders inside the chat panel so it
          feels like part of the assistant ("I'd love to help, but…") instead
          of a popup that interrupts navigation. */}
      {!planLoading && !hasAi ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 text-center bg-gradient-to-br from-indigo-50/40 via-white to-purple-50/40">
          <div className="relative mb-5">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 blur-xl opacity-30" />
            <div className="relative h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/60">
              <Lock className="h-7 w-7 text-white" />
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-100 px-3 py-1 text-[10px] font-bold text-[#6366F1] uppercase tracking-wider mb-3">
            <Sparkles className="h-3 w-3" />
            Growth plan feature
          </div>
          <h3 className="text-base font-bold text-slate-900 mb-1.5">AI Assistant</h3>
          <p className="text-xs text-slate-600 leading-relaxed max-w-[280px]">
            Chat with your CRM data: ask for hot leads, conversion stats, or drafted follow-up messages — powered by Claude.
          </p>
          <p className="text-[11px] text-slate-500 mt-3">
            You're on the <strong className="text-slate-700">{planName}</strong> plan. Upgrade to <strong className="text-[#6366F1]">Growth</strong> to unlock.
          </p>
          <Link
            to="/billing"
            onClick={() => setOpen(false)}
            className="inline-flex items-center gap-1.5 mt-5 px-5 py-2.5 rounded-xl bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
          >
            Upgrade to Growth <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <>
          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <Sparkles className="h-10 w-10 text-indigo-200 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">Hi! I'm your AI assistant</p>
                <p className="text-xs text-slate-400 mt-1">Ask me anything about your leads and CRM</p>
                <div className="flex flex-wrap gap-2 mt-4 justify-center">
                  {QUICK_ACTIONS.map(action => (
                    <button key={action} onClick={() => sendQuery(action)}
                      className="text-xs px-3 py-1.5 rounded-full border border-indigo-200 text-[#6366F1] hover:bg-indigo-50 transition-colors">
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-[#6366F1] text-white rounded-br-sm'
                    : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                }`}>
                  <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                    __html: msg.text
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br>')
                  }} />
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-xl px-4 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-[#6366F1]" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-4 py-3 border-t border-gray-100 flex-shrink-0">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !loading) sendQuery(input); }}
              placeholder="Ask about your leads..."
              className="flex-1 h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              disabled={loading}
            />
            <button
              onClick={() => sendQuery(input)}
              disabled={loading || !input.trim()}
              className="h-9 w-9 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white flex items-center justify-center disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
